import { Box, Grid, Grow, List, ListItem, ListItemText, MenuItem, Paper, Popper, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FormDefinitions, StagePlanPlotPoint, StagePlanPlotPointStatuses, SubStages } from "../../api/dtos";
import Image from "../../components/layout/Image";
import SetLinks from "../../components/layout/SetLinks";
import TitleBox from "../../components/TitleBox";
import LotsContext from "../../contexts/data/LotsContext";
import ProjectContext from "../../contexts/data/ProjectContext";
import StageContext from "../../contexts/data/StageContext";
import { DefaultProjectCreateLink, StagePlanPreviewStageCreateLink } from "../../contexts/layouts/LinkContext";
import useStagePlanByStageIdSubStage from "../../hooks/data/useStagePlanByStageIdSubStage";
import useStagePlanPlotPointsByStageIdSubStage from "../../hooks/data/useStagePlanPlotPointsByStageIdSubStage";
import lotSold from "../../assets/img/stageplan/lot.sold.png";
import lotComingSoon from "../../assets/img/stageplan/lot.comingsoon.png";
import lotForSale from "../../assets/img/stageplan/lot.forsale.png";
import lotDepositTaken from "../../assets/img/stageplan/lot.deposittaken.png";
import lotOnHold from "../../assets/img/stageplan/lot.onhold.png";
import lotNew from "../../assets/img/stageplan/lot.new.png";

interface IStagePlanViewProps { 
    imageUrl?: string
}

const StagePlanView: FC<IStagePlanViewProps> = () => {
    const theme = useTheme();

    const params = useParams();
    const stageId = Number(params.stageId);

    const {data: stage} = useContext(StageContext);
    const {data: project} = useContext(ProjectContext);

    const lotMetadata = useFormMetadata(FormDefinitions.Lot);

    const [fadeTimeout, setFadeTimeout] = useState<NodeJS.Timeout>();

    const [selectedSubStage, setSelectedSubStage] = useState<SubStages | null>();
    const {data: lookups} = useLookups(lotMetadata);
    
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const {data: lots} = useContext(LotsContext);

    const subStages = useMemo(() => {
        return lots?.reduce<SubStages[]>((subs, lot) => 
            lot.subStage && !subs.includes(lot.subStage) 
                ? [...subs, lot.subStage] 
                : subs,[]
    )}, [lots]);

    useEffect(() => {
        setSelectedSubStage(subStages && subStages.length > 0 ? subStages[0] : undefined);
    }, [subStages])

    const {data: stagePlan} = useStagePlanByStageIdSubStage(stageId, selectedSubStage ?? undefined);

    const {data: plotPoints} = useStagePlanPlotPointsByStageIdSubStage(stageId, selectedSubStage ?? undefined);

    const lotsToUse = useMemo(() => {
        return lots?.filter(lot => lot.subStage == selectedSubStage);
    }, [lots, selectedSubStage])

    const [selectedPoint, setSelectedPoint] = useState<StagePlanPlotPoint | null>(null);

    const [imgDomElement, setImgDomElement] = useState<HTMLImageElement | null>(null);

    const [imgDimensions, setImgDimensions] = useState<{x: number; y: number; } | undefined>();


    useEffect(() => {
        if (!imgDomElement) {
            setImgDimensions(undefined);
            return;
        }

        imgDomElement.onload = () => {
            setImgDimensions({ y: imgDomElement.naturalHeight, x: imgDomElement.naturalWidth });
        }

    }, [imgDomElement, setImgDimensions])


    const handlePointClick = (event: React.MouseEvent<HTMLDivElement>, _point: StagePlanPlotPoint) => {
        if(anchorEl == event.target) {
            setAnchorEl(null);
        }
        else {
            setAnchorEl(event.target as HTMLDivElement);
        }
        event.stopPropagation();
    }

    const plotDiameter = 2.5;
    const legendDiameter = 16;

    const legend = [
        {
            iconUrl: lotForSale,
            text: "For Sale"
        },
        {
            iconUrl: lotDepositTaken,
            text: "Deposit Taken"
        },
        {
            iconUrl: lotSold,
            text: "Sold"
        },
        {
            iconUrl: lotComingSoon,
            text: "Coming Soon"
        },
        {
            iconUrl: lotOnHold,
            text: "On Hold"
        },
    ]
    return (
        <>
            <SetLinks
                createProjectLink={DefaultProjectCreateLink}
                createStageLink={StagePlanPreviewStageCreateLink}
                lotLinkEnabled={false}
            />
            
            <TitleBox>
                <Typography variant="h2">
                    {project?.name} - {stage?.name} - {
                        selectedSubStage != undefined && 
                        `SubStage ${GetFieldLookupItem(lookups, 'SubStage', selectedSubStage)?.value?.toString()} - `
                    } Stage Plan
                </Typography>
            </TitleBox>
            {
                subStages &&
                <Paper
                    sx={{
                        padding: theme.spacing(2),
                        width: 200
                    }}
                >
                    <TextField
                        label="SubStage"
                        fullWidth
                        variant="outlined"
                        disabled={subStages.length == 0}
                        select
                        value={selectedSubStage ?? ""}
                        onChange={(event) => {
                            setSelectedSubStage(Number(event.target.value))
                        }}
                    >
                        {
                            subStages?.map((subStage, index) => 
                                <MenuItem key={index} value={subStage}><ListItemText primary={GetFieldLookupItem(lookups, 'SubStage', subStage)?.value?.toString()}/></MenuItem>
                            )
                        }
                    </TextField>
                </Paper>
            }
            <Grid
                sx={{
                    mt: theme.spacing(2)
                }}
                gap={{
                    xs: 2
                }}
                container
            >
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 8,
                        xl: 8
                    }}
                    sx={{
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%"
                        }}
                    > 
                        <Popper
                            open={anchorEl != null}
                            anchorEl={anchorEl}
                            sx={{
                                opacity: 0.9,
                                width: 200
                            }}
                            transition
                        >
                            {({TransitionProps}) => (
                                <Grow {...TransitionProps} timeout={350}>
                                    <Paper 
                                        sx={{
                                            padding: theme.spacing(1)
                                        }}
                                    >
                                        <List
                                            dense
                                        >
                                            <ListItem>
                                                <ListItemText 
                                                    primary={
                                                        `Lot: ${lotsToUse?.filter(lot => lot.id == selectedPoint?.lotId)[0]?.number}`
                                                    }
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        `
                                                            Status: ${
                                                                selectedPoint?.status == StagePlanPlotPointStatuses.ForSale
                                                                ? 'For Sale'
                                                                :  selectedPoint?.status == StagePlanPlotPointStatuses.DepositTaken
                                                                ? 'Deposit Taken'
                                                                :  selectedPoint?.status == StagePlanPlotPointStatuses.ComingSoon
                                                                ? 'Coming Soon'
                                                                :  selectedPoint?.status == StagePlanPlotPointStatuses.OnHold
                                                                ? 'On Hold'
                                                                :  selectedPoint?.status == StagePlanPlotPointStatuses.Sold
                                                                ? 'Sold'
                                                                : 'Unknown'
                                                            }
                                                        `
                                                    }
                                                />
                                            </ListItem>
                                            {
                                                selectedPoint?.size && 
                                                <ListItem>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={
                                                            <Typography variant="body2">
                                                                Size (m<sup>2</sup>): {
                                                                    selectedPoint?.size
                                                                }
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            }
                                            {
                                                selectedPoint?.description &&
                                                <ListItem>
                                                    <ListItemText
                                                        primary={
                                                            selectedPoint?.description
                                                        }
                                                    />
                                                </ListItem>
                                            }
                                        </List>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                        <Image
                            src={stagePlan?.imageUrl}
                            //ref={imgRef}
                            ref={ref => setImgDomElement(ref)}
                            sx={{
                                position: "relative",
                                display: "block",
                                width: "100%"
                            }}
                            
                        />
                        {imgDimensions && imgDomElement && plotPoints?.map((point, i) => {
                            const lot = lotsToUse?.filter(lot => lot.id == point.lotId)[0];
                            const xPercentage = (point!.x!*100.0/imgDimensions.x);
                            const yPercentage = (point!.y!*100.0/imgDimensions.y);
                            let backgroundUrl = lotSold;
                            switch(point.status) {
                                case StagePlanPlotPointStatuses.ForSale:
                                    backgroundUrl = lotForSale;
                                    break;
                                case StagePlanPlotPointStatuses.OnHold:
                                    backgroundUrl = lotOnHold;
                                    break;
                                case StagePlanPlotPointStatuses.DepositTaken:
                                    backgroundUrl = lotDepositTaken;
                                    break;
                                case StagePlanPlotPointStatuses.ComingSoon:
                                    backgroundUrl = lotComingSoon;
                                    break;
                                case StagePlanPlotPointStatuses.Sold:
                                    backgroundUrl = lotSold;
                                    break;
                                default:
                                    backgroundUrl = lotNew;
                                    break;
                            }
                            return (
                            <Tooltip key={i+1}
                                title={
                                    anchorEl 
                                        ? "" 
                                        : lot
                                        ? `Lot ${lot.number}` 
                                        : "No lot"
                                }
                            >
                                <Box
                                    sx={{
                                        fontSize: "0.85rem",
                                        width: `${plotDiameter}%`, 
                                        height: `${plotDiameter}%`, 
                                        position: "absolute",
                                        left: `${xPercentage}%`,
                                        top: `${yPercentage}%`,
                                        borderRadius: plotDiameter/2,
                                        color: "white",
                                        backgroundImage: `url(${backgroundUrl})`,
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: `contain`,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        userSelect: "none",
                                        ":hover" : {
                                            cursor: "pointer"
                                        }
                                    }}
                                    onMouseEnter={(event) => {
                                        setAnchorEl(event.currentTarget);
                                        setSelectedPoint(point);
                                        if(fadeTimeout) clearTimeout(fadeTimeout);
                                    }}
                                    onMouseLeave={(_event) => {
                                        setAnchorEl(null);
                                        setFadeTimeout(
                                            setTimeout(() => {
                                                setSelectedPoint(null);
                                            }, 350)
                                        );
                                    }}
                                    onClick={(event) => handlePointClick(event, point)}
                                    key={i+1}
                                >
                                </Box>
                            </Tooltip>
                        )})}
                    </Box>
                </Grid>
                <Grid>
                    {
                        legend.map((x, i) => (
                            <Box
                                sx={{
                                    display: "flex",
                                    mb: 0.5
                                }}
                                gap={2}
                            >
                                <Box
                                    sx={{
                                        fontSize: "0.85rem",
                                        width: legendDiameter, 
                                        height: legendDiameter, 
                                        borderRadius: legendDiameter/2,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        userSelect: "none",
                                        backgroundImage: `url(${x.iconUrl})`,
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: `contain`,
                                    }}
                                    key={i+1}
                                >
                                </Box>
                                <Typography variant="body2">{x.text}</Typography>
                            </Box>
                        ))
                    }

                </Grid>
            </Grid>
        </>
    )
}

export default StagePlanView;